import { useMemo, useState } from 'react';

import { Button } from 'antd';
import dayjs from 'dayjs';

import { Empty, Skeleton } from '@components/index';
import { DateRangeFilter } from '@components/Filters/DateRangeFilter';
import { Container, Header, StatsRow, Subheader } from '@elements/index';
import { IconArrowDown, IconArrowsSort, IconArrowUp } from '@tabler/icons-react';
import useQueryScheduleParticipationByUser from '@hooks/schedules/useQueryScheduleParticipationByUser';
import useQueryScheduleParticipationCSV from '@hooks/schedules/useQueryScheduleParticipationCSV';
import { OrderByKeys, QueryParametersParticipationsUser } from '@interfaces/scheduleInterface';
import { ScheduleParticipacionUser } from '@interfaces/userInterface';
import orderBy from 'just-order-by';

export const Stats = () => {
	const [parms, setParams] = useState<QueryParametersParticipationsUser>({
		startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
		endDate: dayjs().endOf('year').format('YYYY-MM-DD'),
		orderBy: {
			total: 'DESC'
		}
	});
	const { data: users, isLoading } = useQueryScheduleParticipationByUser(parms);
	const { refetch: refetchCSV } = useQueryScheduleParticipationCSV(parms);

	const sortedUsers = useMemo(() => {
		if (!users) return [];
		const sortKey = (Object.keys(parms.orderBy ?? {})[0] ?? 'total') as keyof ScheduleParticipacionUser;
		const sortDir = parms.orderBy?.[sortKey as OrderByKeys] === 'ASC' ? 'asc' : 'desc';
		const value = users[0]?.[sortKey];
		const iteratee =
			typeof value === 'string' ? (user: ScheduleParticipacionUser) => String(user[sortKey]).toLowerCase() : sortKey;
		return orderBy(users, [{ property: iteratee, order: sortDir }]);
	}, [users, parms.orderBy]);

	const handleSort = (key: OrderByKeys) => {
		setParams((prev) => {
			const currentDirection = prev.orderBy?.[key];
			const newDirection = currentDirection === 'ASC' ? 'DESC' : 'ASC';
			return {
				...prev,
				orderBy: { [key]: newDirection } as Partial<Record<OrderByKeys, 'ASC' | 'DESC'>>
			};
		});
	};

	const downloadCsv = async () => {
		try {
			const blob = await refetchCSV();
			if (blob && blob.data) {
				const url = window.URL.createObjectURL(blob.data);
				const a = document.createElement('a');
				a.href = url;
				a.download = `participation_[${parms.startDate}_${parms.endDate}].csv`;

				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			} else {
				console.error('No data available to download.');
			}
		} catch (error) {
			console.error('Error downloading the CSV file:', error);
		}
	};

	return (
		<div>
			<Header />
			<Container>
				<Subheader
					title="Rapport d'assistances"
					controls={
						<Button type="primary" htmlType="button" onClick={() => downloadCsv()}>
							Télécharger le rapport
						</Button>
					}
				/>

				<div className="flex flex-col gap-5">
					<div>
						<h3 className="font-bold">Filtres</h3>
						<div className="flex">
							<DateRangeFilter
								onChange={(value) =>
									setParams((params) => ({
										...params,
										startDate: value[0],
										endDate: value[1]
									}))
								}
								value={[parms.startDate, parms.endDate]}
							/>
						</div>
					</div>

					<div className="flex items-center justify-between gap-6 px-4 py-2 md:hidden">
						<button
							onClick={() => handleSort('fullName')}
							className="flex items-center gap-1 text-sm font-bold text-slate-500"
						>
							Nom
							{parms.orderBy?.fullName === 'ASC' && <IconArrowUp size={16} className="text-blue-600" />}
							{parms.orderBy?.fullName === 'DESC' && <IconArrowDown size={16} className="text-blue-600" />}
							{!parms.orderBy?.fullName && <IconArrowsSort size={18} className="text-slate-400" />}
						</button>
						<button
							onClick={() => handleSort('total')}
							className="flex items-center gap-1 text-sm font-bold text-slate-500"
						>
							Total
							{parms.orderBy?.total === 'ASC' && <IconArrowUp size={16} className="text-blue-600" />}
							{parms.orderBy?.total === 'DESC' && <IconArrowDown size={16} className="text-blue-600" />}
							{!parms.orderBy?.total && <IconArrowsSort size={18} className="text-slate-400" />}
						</button>
					</div>

					<div className="hidden grid-cols-[3fr_repeat(6,1fr)_1fr] items-center gap-4 rounded-lg px-4 py-1 font-bold text-slate-500 md:grid">
						<button onClick={() => handleSort('fullName')} className="flex items-center gap-1 text-left">
							Nom
							{parms.orderBy?.fullName === 'ASC' && <IconArrowUp size={16} className="text-blue-600" />}
							{parms.orderBy?.fullName === 'DESC' && <IconArrowDown size={16} className="text-blue-600" />}
							{!parms.orderBy?.fullName && <IconArrowsSort size={18} className="text-slate-400" />}
						</button>
						<div>Responsable</div>
						<div>Caissier</div>
						<div>Opérateur</div>
						<div>Contrôleur</div>
						<div>Confiserie</div>
						<div>3D</div>
						<button onClick={() => handleSort('total')} className="flex items-center gap-1 text-left">
							Total
							{parms.orderBy?.total === 'ASC' && <IconArrowUp size={16} className="text-blue-600" />}
							{parms.orderBy?.total === 'DESC' && <IconArrowDown size={16} className="text-blue-600" />}
							{!parms.orderBy?.total && <IconArrowsSort size={18} className="text-slate-400" />}
						</button>
					</div>

					{isLoading && (
						<div className="flex flex-col gap-4">
							<Skeleton height={57} width="100%" />
							<Skeleton height={57} width="100%" />
							<Skeleton height={57} width="100%" />
							<Skeleton height={57} width="100%" />
						</div>
					)}

					{!isLoading && sortedUsers && sortedUsers.length > 0 && (
						<div className="flex flex-col gap-2">
							{sortedUsers.map((user) => (
								<StatsRow key={user.id} user={user} />
							))}
						</div>
					)}

					{!isLoading && users && sortedUsers.length === 0 && (
						<div className="rounded-lg bg-white p-12">
							<Empty
								title="Aucune participation trouvée"
								description="Ajustez les filtres de date pour voir les participations."
							/>
						</div>
					)}
				</div>
			</Container>
		</div>
	);
};
